import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Active/inactive is now a GROUPING in the sidebar (projects with an open
// terminal float into an "Active" section), not a sort mode — so the sort
// modes below only order WITHIN each group.
export type ProjectSortMode = 'last_accessed' | 'date_added' | 'alphabetical';
export type ProjectSortDir = 'asc' | 'desc';
export type SidebarPosition = 'left' | 'right';

/** The natural default direction for a mode: newest-first for time-based,
 *  A→Z for alphabetical. Picking a mode resets direction to this. */
export function naturalSortDir(mode: ProjectSortMode): ProjectSortDir {
  return mode === 'alphabetical' ? 'asc' : 'desc';
}

interface UIState {
  settingsOpen: boolean;
  /** Section ID for the modal to seek to on open. Read-and-cleared by
   *  SettingsModal — set via openSettingsTo() when a caller needs to deep-
   *  link (e.g. the bulk-refresh pill jumping back to Project Maintenance). */
  requestedSettingsSection: string | null;
  projectSortMode: ProjectSortMode;
  /** Sort direction applied within each active/inactive group. */
  projectSortDir: ProjectSortDir;
  /** Persisted expand/collapse state for the "Inactive" project group
   *  (shared across the sidebar, workspace panel, and home dashboard). */
  inactiveGroupExpanded: boolean;
  /** Persisted expand/collapse state for the sidebar's "Archived" section. */
  archivedExpanded: boolean;
  /** Team ("group") ids whose section is collapsed in the grouped project
   *  lists. Teams are expanded by default, so only COLLAPSED ids are stored
   *  (absence = expanded). Shared across the sidebar/panel/home. The special
   *  id `__ungrouped__` collapses the Ungrouped catch-all bucket. */
  collapsedTeams: string[];
  /** Active team filter for the project list. null = "All Projects" (the full
   *  Team → Active/Inactive grouped view). A group id narrows the list to just
   *  that team shown flat (Active/Inactive, no team headers) — a presentation
   *  mode for screen-sharing one team. `__ungrouped__` isolates the Ungrouped
   *  (personal) bucket. Persisted and shared across project surfaces. */
  teamFilter: string | null;
  sidebarPosition: SidebarPosition;
  addProjectDialogOpen: boolean;
  mobileSidebarOpen: boolean;
  /** Global Do Not Disturb — suppresses all notifications when true */
  dndEnabled: boolean;
  /** Optional DND expiry timestamp (ms since epoch). null = indefinite. */
  dndUntil: number | null;
  /** Ref-counted "something is overlaid above the main workspace" flag.
   *  Tauri child webviews (BrowserTab) float over the DOM and would
   *  otherwise paint on top of modals/dialogs — they read this counter
   *  and park themselves offscreen when it's > 0. Any modal that needs
   *  the BrowserTab out of its way should call pushOverlay() on open
   *  and popOverlay() on close (typically in a useEffect). */
  overlayDepth: number;

  openSettings: () => void;
  /** Open Settings and request a specific section. The modal consumes the
   *  requested section in a useEffect and clears it. */
  openSettingsTo: (section: string) => void;
  /** Called by SettingsModal once it has applied the requested section. */
  consumeRequestedSection: () => void;
  closeSettings: () => void;
  /** Set the sort mode; resets direction to that mode's natural default. */
  setProjectSortMode: (mode: ProjectSortMode) => void;
  /** Flip ascending ⇄ descending for the current mode. */
  toggleProjectSortDir: () => void;
  /** Toggle / set the persisted Inactive-group collapse state. */
  toggleInactiveGroup: () => void;
  /** Toggle the persisted Archived-section collapse state. */
  toggleArchived: () => void;
  /** Toggle a team section's collapsed state (by group id, or `__ungrouped__`). */
  toggleTeamCollapsed: (teamId: string) => void;
  /** Set the active team filter (null = All Projects). */
  setTeamFilter: (teamId: string | null) => void;
  setSidebarPosition: (position: SidebarPosition) => void;
  openAddProjectDialog: () => void;
  closeAddProjectDialog: () => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setDnd: (enabled: boolean, until?: number | null) => void;
  /** Check if DND is active (accounts for timer expiry). */
  isDndActive: () => boolean;
  /** Modals call these so child webviews (BrowserTab) can step aside. */
  pushOverlay: () => void;
  popOverlay: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      settingsOpen: false,
      requestedSettingsSection: null,
      projectSortMode: 'last_accessed',
      projectSortDir: 'desc',
      inactiveGroupExpanded: true,
      archivedExpanded: false,
      collapsedTeams: [],
      teamFilter: null,
      sidebarPosition: 'left',
      addProjectDialogOpen: false,
      mobileSidebarOpen: false,
      dndEnabled: false,
      dndUntil: null,
      overlayDepth: 0,

      openSettings: () => set({ settingsOpen: true }),
      openSettingsTo: (section) =>
        set({ settingsOpen: true, requestedSettingsSection: section }),
      consumeRequestedSection: () => set({ requestedSettingsSection: null }),
      closeSettings: () => set({ settingsOpen: false }),
      setProjectSortMode: (mode) => set({ projectSortMode: mode, projectSortDir: naturalSortDir(mode) }),
      toggleProjectSortDir: () => set((s) => ({ projectSortDir: s.projectSortDir === 'asc' ? 'desc' : 'asc' })),
      toggleInactiveGroup: () => set((s) => ({ inactiveGroupExpanded: !s.inactiveGroupExpanded })),
      toggleArchived: () => set((s) => ({ archivedExpanded: !s.archivedExpanded })),
      toggleTeamCollapsed: (teamId) => set((s) => ({
        collapsedTeams: s.collapsedTeams.includes(teamId)
          ? s.collapsedTeams.filter((id) => id !== teamId)
          : [...s.collapsedTeams, teamId],
      })),
      setTeamFilter: (teamId) => set({ teamFilter: teamId }),
      setSidebarPosition: (position) => set({ sidebarPosition: position }),
      openAddProjectDialog: () => set({ addProjectDialogOpen: true }),
      closeAddProjectDialog: () => set({ addProjectDialogOpen: false }),
      toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),
      setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),
      setDnd: (enabled, until = null) => set({ dndEnabled: enabled, dndUntil: until ?? null }),
      isDndActive: () => {
        const { dndEnabled, dndUntil } = get();
        if (!dndEnabled) return false;
        if (dndUntil !== null && Date.now() > dndUntil) {
          // Timer expired — auto-disable
          set({ dndEnabled: false, dndUntil: null });
          return false;
        }
        return true;
      },
      pushOverlay: () => set((s) => ({ overlayDepth: s.overlayDepth + 1 })),
      popOverlay: () => set((s) => ({ overlayDepth: Math.max(0, s.overlayDepth - 1) })),
    }),
    {
      name: 'ateliercode-ui',
      version: 1,
      // v0→v1: 'active' stopped being a sort mode (it's now the sidebar
      // grouping), so remap any persisted 'active' to the default and seed a
      // direction for users upgrading from before projectSortDir existed.
      migrate: (persisted: unknown, version: number) => {
        // Legacy persisted blob — read loosely (old shapes had 'active' as a mode).
        const s = { ...((persisted as Record<string, unknown>) ?? {}) };
        if (version < 1) {
          if (s.projectSortMode === 'active' || s.projectSortMode == null) {
            s.projectSortMode = 'last_accessed';
          }
          if (s.projectSortDir == null) {
            s.projectSortDir = naturalSortDir(s.projectSortMode as ProjectSortMode);
          }
        }
        return s as unknown as UIState;
      },
      partialize: (state) => ({
        projectSortMode: state.projectSortMode,
        projectSortDir: state.projectSortDir,
        inactiveGroupExpanded: state.inactiveGroupExpanded,
        archivedExpanded: state.archivedExpanded,
        collapsedTeams: state.collapsedTeams,
        teamFilter: state.teamFilter,
        sidebarPosition: state.sidebarPosition,
        dndEnabled: state.dndEnabled,
        dndUntil: state.dndUntil,
      }),
    }
  )
);
